import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { useAuth } from '../../auth/AuthProvider';
import { toDateSafe } from '../../lib/dates';
import { conferenceHeaderLabel } from '../../lib/conference';
import { feedbackConferenceId } from '../../lib/adminConferenceFeedback';
import {
  fetchAdminConferenceAttendance,
  fetchAdminFeedbackForms,
  fetchAdminUsageStats,
  fetchAdminQrLanderConfig,
  switchToPresenterSignIn,
  updateAdminQrLanderConfig
} from '../../lib/admin';
import { friendlyRequestError } from '../../lib/http';
import AdminAccessGate from './AdminAccessGate';
import type {
  AdminConferenceAttendance,
  AdminConferenceSessionAttendance,
  AdminFeedbackForm,
  AdminQrLanderConfig,
  AdminUsageStats
} from '../../lib/admin';

function timeValue(input: unknown) {
  return toDateSafe(input as never)?.getTime() ?? 0;
}

function formatSessionTime(start: unknown, end: unknown) {
  const startDate = toDateSafe(start as never);
  const endDate = toDateSafe(end as never);
  const timeFormat = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' });

  if (startDate && endDate) return `${timeFormat.format(startDate)} - ${timeFormat.format(endDate)}`;
  if (startDate) return timeFormat.format(startDate);
  if (endDate) return timeFormat.format(endDate);
  return 'TBD';
}

function formatAdminDateTime(value: unknown) {
  const date = toDateSafe(value as never);
  if (!date) return '';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function formatWholeNumber(value: number) {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
}

function attendanceSlotKey(session: AdminConferenceSessionAttendance) {
  return `${session.startAt || ''}__${session.endAt || ''}`;
}

function roomOrder(session: AdminConferenceSessionAttendance) {
  const normalizedRoom = String(session.room ?? '').trim().toLowerCase();
  if (normalizedRoom === 'music room') return 1;
  if (normalizedRoom === 'williams suite 1') return 2;
  if (normalizedRoom === 'williams suite 2') return 3;

  const match = normalizedRoom.match(/^stream\s+(\d+)/i);
  return match ? Number(match[1]) : 99;
}

function groupSessions(sessions: AdminConferenceSessionAttendance[]) {
  const groups = new Map<string, AdminConferenceSessionAttendance[]>();

  [...sessions]
    .sort((a, b) => timeValue(a.startAt) - timeValue(b.startAt) || roomOrder(a) - roomOrder(b) || a.title.localeCompare(b.title))
    .forEach((session) => {
      const key = attendanceSlotKey(session);
      groups.set(key, [...(groups.get(key) ?? []), session]);
    });

  return Array.from(groups.entries())
    .map(([key, groupedSessions]) => ({
      key,
      sessions: groupedSessions.sort((a, b) => roomOrder(a) - roomOrder(b) || a.title.localeCompare(b.title))
    }))
    .sort((a, b) => timeValue(a.sessions[0]?.startAt) - timeValue(b.sessions[0]?.startAt));
}

function PresenterSwitchPanel() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [switching, setSwitching] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function switchSignIn() {
    setSwitching(true);
    setError(null);
    try {
      await switchToPresenterSignIn();
      await refresh();
      navigate('/conference', { replace: true });
    } catch (err) {
      setError(friendlyRequestError(err, 'Could not switch this sign-in to presenter.'));
    } finally {
      setSwitching(false);
    }
  }

  return (
    <Card>
      <CardHeader title="Presenter Access" subtitle="Prepare this device for conference screen control" />
      <CardBody className="space-y-3">
        <Button onClick={() => void switchSignIn()} disabled={switching}>
          {switching ? 'Switching...' : 'Switch this sign-in to Presenter'}
        </Button>
        {error ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function QrRedirectPanel() {
  const [config, setConfig] = React.useState<AdminQrLanderConfig | null>(null);
  const [targetUrl, setTargetUrl] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const next = await fetchAdminQrLanderConfig();
      setConfig(next);
      setTargetUrl(next.targetUrl || '');
    } catch (err) {
      setError(friendlyRequestError(err, 'Could not load QR redirect.'));
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    void load();
  }, []);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);

    try {
      const result = await updateAdminQrLanderConfig(targetUrl);
      setConfig(result.config);
      setTargetUrl(result.config.targetUrl || targetUrl);
      setMessage(result.changed ? 'QR redirect updated.' : 'No change to save.');
    } catch (err) {
      setError(friendlyRequestError(err, 'Could not update QR redirect.'));
    } finally {
      setSaving(false);
    }
  }

  const updatedAt = formatAdminDateTime(config?.updatedAt);
  const updatedBy = config?.updatedByDisplayName || config?.updatedByEmail || config?.updatedByProfileId || '';

  return (
    <Card>
      <CardHeader title="QR Redirect" subtitle="Destination for /qrlander" />
      <CardBody className="space-y-3">
        <form onSubmit={submit} className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
          <Input
            type="url"
            value={targetUrl}
            onChange={(event) => {
              setTargetUrl(event.target.value);
              setMessage(null);
            }}
            placeholder="https://example.com"
            disabled={loading || saving}
            required
          />
          <Button type="submit" disabled={loading || saving || !targetUrl.trim()}>
            {saving ? 'Updating...' : 'Update'}
          </Button>
        </form>

        {updatedAt || updatedBy ? (
          <div className="text-xs text-zinc-500">
            Last updated{updatedAt ? ` ${updatedAt}` : ''}{updatedBy ? ` by ${updatedBy}` : ''}.
          </div>
        ) : null}

        {message ? <div className="text-xs font-medium text-zinc-700">{message}</div> : null}
        {error ? (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {error}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

function AttendanceCard(props: { session: AdminConferenceSessionAttendance }) {
  return (
    <div className="flex min-h-16 items-start justify-between gap-2 rounded-lg bg-zinc-50 px-2.5 py-2">
      <div className="min-w-0 text-sm font-medium leading-tight text-zinc-900">{props.session.title}</div>
      <div className="min-w-8 rounded-md bg-zinc-900 px-2 py-1 text-center text-sm font-semibold leading-none text-white">
        {props.session.planningCount}
      </div>
    </div>
  );
}

function FeedbackTotalsPanel(props: { forms: AdminFeedbackForm[]; title?: string }) {
  const prizeFeedbackFormId = '2026-conference-feedback';
  const forms = [...props.forms].reverse();

  return (
    <Card>
      <CardHeader
        title={props.title || 'Feedback'}
        subtitle="Submitted feedback forms"
      />
      <CardBody>
        {forms.length ? (
          <div className="space-y-2">
            {forms.map((form) => {
              const formPath = `/feedback/${encodeURIComponent(form.id)}`;
              return (
                <div
                  key={form.id}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
                >
                  <Link
                    to={formPath}
                    className="min-w-0 rounded-md text-sm font-medium text-zinc-800 outline-none hover:text-accent focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    <span className="block truncate">{form.title || form.id}</span>
                  </Link>
                  {form.id === prizeFeedbackFormId ? (
                    <Link to="/admin/prizes" className="shrink-0">
                      <Button variant="outline">Prizes</Button>
                    </Link>
                  ) : null}
                  <Link
                    to={formPath}
                    aria-label={`Open ${form.title || form.id}`}
                    className="min-w-8 rounded-md bg-zinc-900 px-2.5 py-1 text-center text-sm font-semibold leading-none text-white hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
                  >
                    {form.responseCount || 0}
                  </Link>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
            No feedback forms found.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function ConferenceAttendancePanel(props: {
  conference: AdminConferenceAttendance;
  loading: boolean;
  onRefresh: () => void;
}) {
  return (
    <Card>
      <CardHeader
        title="Attendance Summary"
        subtitle="Conference session choices"
        right={
          <Button variant="ghost" onClick={props.onRefresh} disabled={props.loading}>
            {props.loading ? 'Loading...' : 'Refresh'}
          </Button>
        }
      />
      <CardBody className="space-y-4">
        {props.conference.sessions.length ? (
          <div className="space-y-2">
            {groupSessions(props.conference.sessions).map((group) => (
              <div key={group.key} className="rounded-xl border border-zinc-200 bg-white p-2">
                <div className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  {formatSessionTime(group.sessions[0]?.startAt, group.sessions[0]?.endAt)}
                </div>
                <div className={['grid grid-cols-1 gap-2', group.sessions.length > 1 ? 'lg:grid-cols-3' : ''].join(' ')}>
                  {group.sessions.map((session) => (
                    <AttendanceCard key={session.id} session={session} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
            No attendance-enabled sessions found.
          </div>
        )}
      </CardBody>
    </Card>
  );
}

function UsageStatsPanel(props: {
  stats: AdminUsageStats | null;
  loading: boolean;
  onRefresh: () => void;
}) {
  const [companySort, setCompanySort] = React.useState<{
    column: 'companyName' | 'profiles';
    direction: 'ascending' | 'descending';
  } | null>(null);
  const totals = props.stats?.totals ?? { posts: 0, replies: 0, likes: 0, profiles: 0 };
  const recentMonths = props.stats?.months.slice(0, 12) ?? [];
  const companies = [...(props.stats?.profilesByCompany ?? [])];
  if (companySort) {
    companies.sort((a, b) => {
      const comparison = companySort.column === 'companyName'
        ? a.companyName.localeCompare(b.companyName)
        : a.profiles - b.profiles;
      return companySort.direction === 'ascending' ? comparison : -comparison;
    });
  }

  function sortCompanies(column: 'companyName' | 'profiles') {
    setCompanySort((current) => ({
      column,
      direction: current?.column === column && current.direction === 'ascending' ? 'descending' : 'ascending'
    }));
  }

  return (
    <Card>
      <CardHeader
        title="Usage Summary"
        subtitle="Discussion activity and member profiles"
        right={
          <Button variant="ghost" onClick={props.onRefresh} disabled={props.loading}>
            {props.loading ? 'Loading...' : 'Refresh'}
          </Button>
        }
      />
      <CardBody className="space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          {[
            { label: 'Posts', value: totals.posts },
            { label: 'Replies', value: totals.replies },
            { label: 'Likes', value: totals.likes },
            { label: 'Profiles', value: totals.profiles }
          ].map((item) => (
            <div key={item.label} className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">{item.label}</div>
              <div className="mt-1 text-2xl font-semibold leading-none text-zinc-900">
                {formatWholeNumber(item.value)}
              </div>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="min-w-0">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">By Month</div>
            {recentMonths.length ? (
              <div className="overflow-x-auto rounded-lg border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    <tr>
                      <th className="px-3 py-2">Month</th>
                      <th className="px-3 py-2 text-right">Posts</th>
                      <th className="px-3 py-2 text-right">Replies</th>
                      <th className="px-3 py-2 text-right">Likes</th>
                      <th className="px-3 py-2 text-right">Profiles</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {recentMonths.map((month) => (
                      <tr key={month.month}>
                        <td className="px-3 py-2 font-medium text-zinc-800">{month.label}</td>
                        <td className="px-3 py-2 text-right text-zinc-700">{formatWholeNumber(month.posts)}</td>
                        <td className="px-3 py-2 text-right text-zinc-700">{formatWholeNumber(month.replies)}</td>
                        <td className="px-3 py-2 text-right text-zinc-700">{formatWholeNumber(month.likes)}</td>
                        <td className="px-3 py-2 text-right text-zinc-700">{formatWholeNumber(month.profiles)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
                No dated usage records found.
              </div>
            )}
          </div>

          <div className="min-w-0">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Profiles By Company</div>
            {companies.length ? (
              <div className="max-h-80 overflow-auto rounded-lg border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500">
                    <tr>
                      {(['companyName', 'profiles'] as const).map((column) => (
                        <th
                          key={column}
                          scope="col"
                          aria-sort={companySort?.column === column ? companySort.direction : 'none'}
                          className={column === 'profiles' ? 'text-right' : 'text-left'}
                        >
                          <button
                            type="button"
                            onClick={() => sortCompanies(column)}
                            className={`inline-flex w-full items-center gap-1 px-3 py-2 text-xs font-semibold uppercase tracking-wide hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40 ${column === 'profiles' ? 'justify-end' : ''}`}
                          >
                            {column === 'companyName' ? 'Company' : 'Profiles'}
                          </button>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-200">
                    {companies.map((company) => (
                      <tr key={company.companyId || company.companyName}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-zinc-800">{company.companyName}</div>
                        </td>
                        <td className="px-3 py-2 text-right text-zinc-700">{formatWholeNumber(company.profiles)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
                No company profile data found.
              </div>
            )}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function AdminContent() {
  const [forms, setForms] = React.useState<AdminFeedbackForm[]>([]);
  const [conferenceAttendance, setConferenceAttendance] = React.useState<AdminConferenceAttendance[]>([]);
  const [usageStats, setUsageStats] = React.useState<AdminUsageStats | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setError(null);

    const [formsResult, attendanceResult, usageStatsResult] = await Promise.allSettled([
      fetchAdminFeedbackForms(),
      fetchAdminConferenceAttendance(),
      fetchAdminUsageStats()
    ]);

    if (formsResult.status === 'fulfilled') {
      setForms(formsResult.value);
    }
    if (attendanceResult.status === 'fulfilled') {
      setConferenceAttendance(attendanceResult.value);
    }
    if (usageStatsResult.status === 'fulfilled') {
      setUsageStats(usageStatsResult.value);
    }

    const failures = [
      formsResult.status === 'rejected'
        ? `Feedback responses: ${friendlyRequestError(formsResult.reason, 'Could not load feedback responses.')}`
        : '',
      attendanceResult.status === 'rejected'
        ? `Conference attendance: ${friendlyRequestError(attendanceResult.reason, 'Could not load conference attendance.')}`
        : '',
      usageStatsResult.status === 'rejected'
        ? `Usage stats: ${friendlyRequestError(usageStatsResult.reason, 'Could not load usage stats.')}`
        : ''
    ].filter(Boolean);

    if (failures.length) {
      setError(`Could not refresh all admin data. ${failures.join(' ')}`);
    }

    setLoading(false);
  }

  React.useEffect(() => {
    void refresh();
  }, []);

  const conferences = [...conferenceAttendance].sort((a, b) => timeValue(b.startAt) - timeValue(a.startAt));
  const formsByConference = new Map<string, AdminFeedbackForm[]>();
  const otherForms: AdminFeedbackForm[] = [];
  for (const form of forms) {
    const conferenceId = feedbackConferenceId(form, conferences);
    if (conferenceId) {
      formsByConference.set(conferenceId, [...(formsByConference.get(conferenceId) || []), form]);
    } else {
      otherForms.push(form);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4">
      {error ? (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <QrRedirectPanel />
        <PresenterSwitchPanel />
      </div>
      <UsageStatsPanel
        stats={usageStats}
        loading={loading}
        onRefresh={() => void refresh()}
      />
      {conferences.map((conference) => (
        <details key={conference.id} className="group overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm">
          <summary className="flex cursor-pointer list-none items-center gap-3 bg-zinc-200 px-4 py-3 text-sm font-semibold text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/40 [&::-webkit-details-marker]:hidden">
            <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" aria-hidden />
            <span>{conferenceHeaderLabel(conference.startAt)}</span>
          </summary>
          <div className="grid grid-cols-1 gap-4 border-t border-zinc-300 p-4">
            <ConferenceAttendancePanel
              conference={conference}
              loading={loading}
              onRefresh={() => void refresh()}
            />
            <FeedbackTotalsPanel forms={formsByConference.get(conference.id) || []} />
          </div>
        </details>
      ))}
      {!conferences.length ? (
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-600">
          {loading ? 'Loading conferences...' : 'No conferences found.'}
        </div>
      ) : null}
      {otherForms.length ? <FeedbackTotalsPanel forms={otherForms} title="Other Feedback" /> : null}
    </div>
  );
}

export default function AdminPage() {
  return (
    <AdminAccessGate>
      <AdminContent />
    </AdminAccessGate>
  );
}
